import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import GradientButton from '../components/GradientButton'
import Drawer from '../components/Drawer'
import PillTabs from '../components/PillTabs'
import CrewModeFarmFlagsPanel from './crewMode/CrewModeFarmFlagsPanel'
import styles from './EditFarm.module.css'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { useBlockingConfirm } from '../hooks/useBlockingConfirm'
import { useT } from '../i18n'
import { getProductionSpeciesDescription, getProductionSpeciesLabel } from '../utils/productionSpeciesLabels'
import type { FacilityType } from '../types/farmWizard'
import type { EntityBatchSyncStatus, EntityMutationSyncState } from '../utils/entityMutationSync'
import {
  ensureOfflineReady,
  syncLotsFromDbIntoLocalStorage,
  syncLotsFromLocalStorageIntoDb
} from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { logEvent } from '../utils/log'
import { dexieFarmRepo } from '../offline/repos/DexieFarmRepo'
import { dexieLotRepo } from '../offline/repos/DexieLotRepo'
import { dexieTaskRepo } from '../offline/repos/DexieTaskRepo'
import { enqueueEntityChanges } from '../offline/outbox/entityOutbox'
import { syncEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { readLotsFromLocalStorage } from '../offline/lots/lotsLocalStorageMirror'
import { deleteWaterTableRows } from '../utils/waterTable'
import { deleteProductionUnitTableDataset, replaceProductionUnitTableRows, toProductionRecordPayload } from '../utils/productionUnitTable'
import { purgeLocalDataForProductionUnit } from '../utils/entityCachePurge'
import { getBrowserTimeZone, getTimeZoneFromFarm, isValidTimeZone, resolveTimeZone } from '../utils/timeBucketing'
import {
  PRODUCTION_SPECIES_OPTIONS,
  PRODUCTION_SPECIES_PRESETS,
  normalizeSpeciesKey,
  type ProductionSpeciesKey
} from '../data/productionSpeciesPresets'
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import {
  applyImportedStoragePayload,
  buildSundayAppExportPayload
} from '../utils/editFarmToolStorage'
import { ensureFarmList } from '../utils/farmWizard'
import {
  deleteEditedFarm,
  readEditFacilitySyncStatus,
  retryEditFacilitySyncStatusWithReport,
  saveEditedFarm,
  type DeleteFarmMutationResult,
  type EditFarmSyncTarget,
  type EditFarmMutationResult
} from '../utils/editFarmApi'
import { deriveEditFarmSyncOutcome, type EditFarmSyncOutcome } from '../utils/editFarmSyncOutcome'
import { computeSyncBannerVisibility, shouldPublishRefreshComplete } from '../utils/syncBannerVisibility'
import { reconcileSyncMutationState } from '../utils/syncMutationReconciler'
import {
  formatFarmProcessingSitesDraft,
  parseFarmProcessingSitesDraft
} from '../utils/farmProcessingSites'
import {
  loadActiveContextSelection,
  persistActiveContextSelection
} from '../utils/activeContextSelection'
import { generateUuid } from '../utils/uuid'
import { api, ApiError, type RecordHoldings } from '../utils/api'
import { useLots } from '../hooks/useLots'
import { crewOrderOf, isHiddenFromCrew, isInactive, sortUnitsForCrew, withCrewSettings, withStatus } from '../utils/unitStatus'
import { resolveEditFarmPreferredFarm } from './editFarm/preferredFarmSelection'
import UnitCrewPlansEditor, { type UnitCrewPlans } from './editFarm/UnitCrewPlansEditor'
import FacilityProjectionSettings from './editFarm/FacilityProjectionSettings'
import FacilityHatcherySettings from './editFarm/FacilityHatcherySettings'
import { UNIT_TYPE_LABEL_KEY } from './capacity/CapacityTableEditor'
import { UNIT_TYPES, isUnitType } from '../utils/density'
import { FACILITY_CURRENCIES, readFacilityCurrency } from '../utils/facilityCurrency'
import { withHatcheryDefaults, type HatcheryDefaults } from '../utils/hatchery'
import { withTransferProtocol, type TransferProtocolBand } from '../utils/transferProtocol'
import FacilityTransferProtocol from './editFarm/FacilityTransferProtocol'
import { withHarvestTargetWeightG, withProjectionTgc } from '../utils/lotProjectionSettings'
import { inventoryApi } from '../utils/inventoryApi'
import { readMix, validateMix } from '../utils/feedMix'
import { copyUnitPlans, withUnitPlans } from '../utils/unitPlans'
import { copyRulesAndMeasuringPoint, withMeasuringPoint } from '../utils/measuringPoint'
import type { InventoryItem } from '../types/inventory'
import { farmToday } from '../utils/farmTime'

type Farm = {
  id: string
  name: string
  facilityType?: FacilityType
  altitude?: number | null
  location?: string | null
  metadata?: Record<string, unknown>
  createdAt?: string
  updatedAt?: string
  productionUnits: ProductionUnit[]
}

type ProductionUnit = {
  id: string
  farmId: string
  name: string
  altitude?: number | null
  metadata?: Record<string, unknown>
  createdAt?: string
  updatedAt?: string
  capacity?: number | null
  capacityUnit?: string
  type?: string
}

type StatusMessage = {
  tone: 'info' | 'success' | 'error'
  message: string
}

const resolveFacilityType = (farm?: Pick<Farm, 'facilityType' | 'metadata'> | null): FacilityType => {
  if (farm?.facilityType === 'processing-plant') return 'processing-plant'
  return farm?.metadata?.facilityType === 'processing-plant' ? 'processing-plant' : 'farm'
}

const readRequestedFacilityId = (search: string): string | null => {
  const value = new URLSearchParams(search).get('facilityId')
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

type TFunction = ReturnType<typeof useT>

/** Pure string-builder for the sensitive-change confirm message (no side effects). */
type SensitiveFieldKey = 'facilityType' | 'altitude' | 'timeZone' | 'speciesPreset'

// Office UX v0 (findings #94): General saves every field at once, so the
// confirmation names each sensitive field that changed.
const sensitiveChangeMessage = (
  t: TFunction,
  fieldKeys: SensitiveFieldKey[]
): string => t('editFarm.confirm.sensitiveChange', {
  field: fieldKeys.map((fieldKey) => t(`editFarm.sensitiveField.${fieldKey}`)).join(', ')
})

type FacilityTab = 'general' | 'units' | 'culture' | 'advanced'

// Findings #127: "R1", " r1 " and "R1" are the same unit name.
const normalizeUnitName = (name: string | null | undefined): string => (name ?? '').trim().toLocaleLowerCase('es')

export default function EditFarm() {
  const location = useLocation()
  const navigate = useNavigate()
  const t = useT()
  const unitTypeLabel = (type: string | undefined): string => {
    if (!type) return '—'
    return isUnitType(type) ? t(UNIT_TYPE_LABEL_KEY[type]) : t('editFarm.productionUnits.typeOther', { type })
  }
  const principal = useStoredPrincipal()
  const canManageFarms = !principal || principal.role === 'editor' || principal.role === 'admin'
  const { lots: editFarmLots } = useLots({ enabled: true })
  const exportButtonRef = useRef<HTMLDivElement | null>(null)
  const importInputRef = useRef<HTMLInputElement | null>(null)
  const [highlightExport, setHighlightExport] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isApplyingMutation, setIsApplyingMutation] = useState(false)
  const [isSyncingNow, setIsSyncingNow] = useState(false)
  const [isRetryingFacilitySync, setIsRetryingFacilitySync] = useState(false)
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null)
  const [facilitySyncStatus, setFacilitySyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  const facilitySyncStatusRef = useRef<EntityBatchSyncStatus | null>(null)
  const [facilitySyncLastOutcome, setFacilitySyncLastOutcome] = useState<(EditFarmSyncOutcome & { attemptedAtIso: string }) | null>(null)
  const statusVersionRef = useRef(0)
  // v2.1 finding #61 (WSL-09, 2026-07-01): forward-ref indirection so
  // `applyMutationStatus` can trigger a post-drain outbox-status refresh
  // from its `syncPromise.then` callback. The `useEffect` after the
  // `refreshFacilitySyncStatus` declaration keeps the ref pointed at a
  // closure that re-reads the CURRENT `selectedFacilitySyncTarget`.
  const refreshFacilitySyncStatusFnRef = useRef<() => Promise<EntityBatchSyncStatus | null>>(async () => null)
  const confirmModal = useBlockingConfirm()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('focus') !== 'export') return
    const node = exportButtonRef.current
    if (!node) return
    node.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlightExport(true)
    globalThis.setTimeout(() => setHighlightExport(false), 2800)
  }, [location.search])

  const [farms, setFarms] = useState<Farm[]>([])
  const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null)
  // A unit with an Active lot cannot be set Inactiva (harvest or move the lot
  // first); crew positions follow the office order (findings #76, Field Bundle v0).
  const activeLotKeysByUnit = new Map<string, string[]>()
  for (const lot of editFarmLots) {
    if (!lot.productionUnitId || (lot.stage && lot.stage !== 'Active')) continue
    if (selectedFarm && lot.farmId !== selectedFarm.id) continue
    activeLotKeysByUnit.set(lot.productionUnitId, [...(activeLotKeysByUnit.get(lot.productionUnitId) ?? []), lot.lotKey])
  }
  const crewPositionById = new Map(
    sortUnitsForCrew(selectedFarm?.productionUnits ?? []).map((pu, index) => [pu.id, index] as const)
  )
  // Office Plans v0: feeds (and valid mixes) the unit's feed plan can use.
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([])
  useEffect(() => {
    let cancelled = false
    void inventoryApi.listAll().then((items) => {
      if (!cancelled) setInventoryItems(items)
    }).catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])
  const farmFeedItems = inventoryItems.filter((item) => item.farmId === selectedFarm?.id && item.category === 'feed')
  const planFeedItems = farmFeedItems.filter((item) => {
    const mix = readMix(item)
    return !mix || validateMix(mix, farmFeedItems).ok
  })
  const activeLotsForUnit = (puId: string) => editFarmLots
    .filter((lot) => lot.productionUnitId === puId && (!lot.stage || lot.stage === 'Active') && (!selectedFarm || lot.farmId === selectedFarm.id))
    .map((lot) => ({ id: lot.id, lotKey: lot.lotKey, lineageRootId: lot.lineageRootId ?? null }))
  const [fellBackToFirstFarm, setFellBackToFirstFarm] = useState(false)
  const [farmQuery, setFarmQuery] = useState('')
  const [farmNameDraft, setFarmNameDraft] = useState<string>('')
  const [facilityTab, setFacilityTab] = useState<FacilityTab>('general')
  const [openUnitId, setOpenUnitId] = useState<string | null>(null)
  const [puNameError, setPuNameError] = useState<string | null>(null)
  const [editingPU, setEditingPU] = useState<string>('')
  const [puNameDraft, setPuNameDraft] = useState<string>('')
  const [editingPUCapacity, setEditingPUCapacity] = useState<string>('')
  const [puCapacityDraft, setPuCapacityDraft] = useState<string>('')
  const [puCapacityUnitDraft, setPuCapacityUnitDraft] = useState<string>('m3')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{type: 'farm' | 'pu', id: string} | null>(null)
  // findings #76 item 2: a unit / facility with records is set Inactiva, not deleted.
  const [blockedDelete, setBlockedDelete] = useState<{ type: 'farm' | 'pu'; id: string; holdings: RecordHoldings } | null>(null)
  const [checkingDelete, setCheckingDelete] = useState(false)
  const [farmTimeZoneDraft, setFarmTimeZoneDraft] = useState<string>('')
  const [farmCurrencyDraft, setFarmCurrencyDraft] = useState<string>('')
  const [farmSpeciesKeyDraft, setFarmSpeciesKeyDraft] = useState<ProductionSpeciesKey>('trout')
  const [farmProcessingSitesDraft, setFarmProcessingSitesDraft] = useState<string>('')
  const [facilityTypeDraft, setFacilityTypeDraft] = useState<FacilityType>('farm')
  const [altitudeDraft, setAltitudeDraft] = useState<string>('0')

  const repairFarmsMissingProductionUnitsFromLots = async (): Promise<void> => {
    try {
      await ensureOfflineReady()
      const [farmsFromDb, lotsFromDb] = await Promise.all([
        dexieFarmRepo.list(),
        dexieLotRepo.list()
      ])

      if (!Array.isArray(farmsFromDb) || farmsFromDb.length === 0) return

      const unitIdsByFarmId = new Map<string, Set<string>>()
      ;(Array.isArray(lotsFromDb) ? lotsFromDb : []).forEach((lot) => {
        const farmId = String((lot as any)?.farmId ?? '').trim()
        const productionUnitId = String((lot as any)?.productionUnitId ?? '').trim()
        if (!farmId || !productionUnitId) return

        let set = unitIdsByFarmId.get(farmId)
        if (!set) {
          set = new Set()
          unitIdsByFarmId.set(farmId, set)
        }
        set.add(productionUnitId)
      })

      const updatedFarms = farmsFromDb.map((farm: any) => {
        const farmId = String(farm?.id ?? '').trim()
        const existingUnits = Array.isArray(farm?.productionUnits) ? farm.productionUnits : []
        if (existingUnits.length > 0) return farm

        const derivedUnitIds = Array.from(unitIdsByFarmId.get(farmId)?.values() ?? []).sort((a, b) => a.localeCompare(b))
        const derivedUnits = derivedUnitIds.map((unitId, index) => ({
          id: unitId,
          farmId,
          name: `PU ${index + 1}`
        }))
        if (derivedUnits.length === 0) {
          return {
            ...farm,
            productionUnits: []
          }
        }

        return {
          ...farm,
          productionUnits: derivedUnits
        }
      })

      await dexieFarmRepo.upsertMany(updatedFarms as any)
    } catch (error) {
      console.warn('[editFarm] failed to repair production units from lots', error)
    }
  }

  const filteredFarms = React.useMemo(() => {
    const query = farmQuery.trim().toLowerCase()
    if (!query) return farms
    return farms.filter((farm) => {
      const name = String(farm.name ?? '').toLowerCase()
      return name.includes(query)
    })
  }, [farms, farmQuery])

  const selectedSpeciesLabel = selectedFarm
    && resolveFacilityType(selectedFarm) === 'farm'
    ? getProductionSpeciesLabel(t, farmSpeciesKeyDraft)
    : null

  const selectedFacilityType = resolveFacilityType(selectedFarm)
  const selectedFarmAltitude = selectedFarm?.altitude ?? 0
  const selectedFarmTimeZone = selectedFarm ? (getTimeZoneFromFarm(selectedFarm) ?? '') : ''
  const selectedFarmCurrency = readFacilityCurrency(selectedFarm) ?? ''
  const selectedFarmSpeciesKey = selectedFarm ? (resolveFarmProductionSpeciesKey(selectedFarm) ?? 'trout') : 'trout'
  const selectedFacilityIsProcessingPlant = selectedFacilityType === 'processing-plant'
  const selectedFacilitySyncTarget = React.useMemo<EditFarmSyncTarget>(() => {
    if (!selectedFarm) {
      return {
        farmId: null,
        productionUnitIds: []
      }
    }

    return {
      farmId: String(selectedFarm.id ?? '').trim() || null,
      productionUnitIds: (Array.isArray(selectedFarm.productionUnits) ? selectedFarm.productionUnits : [])
        .map((unit) => String(unit?.id ?? '').trim())
        .filter(Boolean)
    }
  }, [selectedFarm])

  const facilitySyncAttentionMessage = React.useMemo(() => {
    if (!facilitySyncStatus || facilitySyncStatus.syncState === 'synced') return null

    if (facilitySyncStatus.blockedCount > 0) {
      return facilitySyncStatus.errorMessage
        ? t('editFarm.sync.blockedWithReason', {
          count: facilitySyncStatus.blockedCount,
          reason: facilitySyncStatus.errorMessage
        })
        : t('editFarm.sync.blocked', { count: facilitySyncStatus.blockedCount })
    }

    if (facilitySyncStatus.syncState === 'offline') {
      return t('editFarm.sync.offline')
    }

    return t('editFarm.sync.pending')
  }, [facilitySyncStatus, t])

  const facilitySyncLastResultSummary = React.useMemo(() => {
    if (!facilitySyncLastOutcome) return null

    const resultLabel = t(facilitySyncLastOutcome.resultKey, facilitySyncLastOutcome.resultParams)
    const attemptedAtLabel = new Date(facilitySyncLastOutcome.attemptedAtIso).toLocaleString()

    return {
      resultLabel: t('editFarm.sync.lastResult', { result: resultLabel }),
      attemptedAtLabel: t('editFarm.sync.lastAttempted', { time: attemptedAtLabel })
    }
  }, [facilitySyncLastOutcome, t])

  // Load farms on component mount
  useEffect(() => {
    void loadFarms()
  }, [])

  const loadFarms = async (options: { forceRefresh?: boolean; preferredFarmId?: string | null } = {}) => {
    try {
      await ensureOfflineReady()
      const farmsData = await ensureFarmList({ forceRefresh: options.forceRefresh })
      // Validate and clean the data
      const validatedFarms = Array.isArray(farmsData) ? farmsData.filter((farm: any) => 
        farm && typeof farm === 'object' && farm.id && farm.name
      ).map((farm: any) => ({
        ...farm,
        productionUnits: Array.isArray(farm.productionUnits) ? farm.productionUnits.filter((pu: any) => 
          pu && typeof pu === 'object' && pu.id && pu.name
        ) : []
      })) : []
      
      setFarms(validatedFarms)

      // v2.1 finding #29: resolve which farm to show after reload using a
      // documented priority (explicit > URL ?facilityId > active-context > in-memory)
      // before falling back to the first farm. The fallback now flips a banner
      // so the operator knows the selection was demoted.
      const activeContextFarmId = loadActiveContextSelection().farmId
      const resolution = resolveEditFarmPreferredFarm({
        validatedFarmIds: validatedFarms.map((farm) => farm.id),
        explicitPreferred: options.preferredFarmId ?? null,
        urlFacilityId: readRequestedFacilityId(location.search),
        activeContextFarmId,
        inMemorySelectedId: selectedFarm?.id ?? null
      })
      const nextSelected = resolution.preferredFarmId
        ? validatedFarms.find((farm) => farm.id === resolution.preferredFarmId) ?? null
        : null

      setSelectedFarm(nextSelected)
      setFellBackToFirstFarm(resolution.fellBackToFirst)

      // Mirror the resolved selection into the URL + active-context store so
      // the next reload (or a navigation to Biomass/Database) survives.
      if (nextSelected) {
        syncFarmSelectionSideEffects(nextSelected.id)
      }
    } catch (error) {
      console.error('Error loading farms data:', error)
      setFarms([])
      setSelectedFarm(null)
      setFellBackToFirstFarm(false)
    }
  }

  // v2.1 finding #29: keep the URL `?facilityId=<id>` and the tenant-scoped
  // active-context farm in sync with whatever the operator picked. Used both
  // by the farm list click handler and after a successful save / reload.
  const syncFarmSelectionSideEffects = (farmId: string) => {
    persistActiveContextSelection({ farmId })
    const search = new URLSearchParams(location.search)
    const currentParam = search.get('facilityId')?.trim() ?? ''
    if (currentParam === farmId) return
    search.set('facilityId', farmId)
    navigate({ pathname: location.pathname, search: `?${search.toString()}` }, { replace: true })
  }

  const selectFarm = (farm: Farm) => {
    setSelectedFarm(farm)
    setFellBackToFirstFarm(false)
    syncFarmSelectionSideEffects(farm.id)
  }


  const refreshFacilitySyncStatus = async (target: EditFarmSyncTarget): Promise<EntityBatchSyncStatus> => {
    const status = await readEditFacilitySyncStatus(target)
    setFacilitySyncStatus(status)
    return status
  }

  // v2.1 finding #61 (WSL-09): keep the post-drain refresh ref pointed at
  // a closure that re-reads the CURRENT `selectedFacilitySyncTarget` so
  // `applyMutationStatus` (declared below) can call through it from its
  // `syncPromise.then` callback. See the ref's declaration block above
  // for the full rationale.
  useEffect(() => {
    refreshFacilitySyncStatusFnRef.current = async () => {
      try {
        return await refreshFacilitySyncStatus(selectedFacilitySyncTarget)
      } catch {
        return null
      }
    }
  }, [selectedFacilitySyncTarget])

  const applyFacilitySyncOutcome = (status: EntityBatchSyncStatus, skippedNotFoundCount: number) => {
    const outcome = deriveEditFarmSyncOutcome({
      status,
      skippedNotFoundCount
    })

    setFacilitySyncLastOutcome({
      ...outcome,
      attemptedAtIso: new Date().toISOString()
    })
  }

  const handleFacilitySyncRetry = async (): Promise<void> => {
    if (!selectedFacilitySyncTarget.farmId && selectedFacilitySyncTarget.productionUnitIds.length === 0) {
      setFacilitySyncStatus(null)
      return
    }

    setIsRetryingFacilitySync(true)

    try {
      const report = await retryEditFacilitySyncStatusWithReport(selectedFacilitySyncTarget, {
        maxEntries: 200,
        maxPasses: 5
      })

      setFacilitySyncStatus(report.status)
      applyFacilitySyncOutcome(report.status, report.skippedNotFoundCount)
      await loadFarms({ forceRefresh: true, preferredFarmId: selectedFarm?.id ?? null })
    } catch {
      showStatus('error', t('editFarm.sync.retryFailed'))
    } finally {
      setIsRetryingFacilitySync(false)
    }
  }

  const handleFacilitySyncRefresh = async (): Promise<void> => {
    try {
      const preferredFarmId = selectedFarm?.id ?? null
      await loadFarms({ forceRefresh: true, preferredFarmId })
      const status = await refreshFacilitySyncStatus(selectedFacilitySyncTarget)
      applyFacilitySyncOutcome(status, 0)
      showStatus('success', t('editFarm.sync.refreshComplete'))
    } catch {
      showStatus('error', t('editFarm.sync.refreshFailed'))
    }
  }

  useEffect(() => {
    let active = true

    void (async () => {
      if (!selectedFacilitySyncTarget.farmId && selectedFacilitySyncTarget.productionUnitIds.length === 0) {
        if (active) setFacilitySyncStatus(null)
        return
      }

      try {
        const status = await readEditFacilitySyncStatus(selectedFacilitySyncTarget)
        if (!active) return
        setFacilitySyncStatus(status)
      } catch {
        if (!active) return
        setFacilitySyncStatus(null)
      }
    })()

    return () => {
      active = false
    }
  }, [selectedFacilitySyncTarget])

  useEffect(() => {
    facilitySyncStatusRef.current = facilitySyncStatus
  }, [facilitySyncStatus])

  useEffect(() => {
    if (!selectedFarm) {
      setFarmTimeZoneDraft('')
      return
    }
    const current = getTimeZoneFromFarm(selectedFarm) ?? ''
    setFarmTimeZoneDraft(current)
    setFarmCurrencyDraft(readFacilityCurrency(selectedFarm) ?? '')
  }, [selectedFarm?.id])

  useEffect(() => {
    if (!selectedFarm) {
      setFarmSpeciesKeyDraft('trout')
      return
    }
    const resolved = resolveFarmProductionSpeciesKey(selectedFarm)
    setFarmSpeciesKeyDraft(resolved ?? 'trout')
  }, [selectedFarm?.id])

  useEffect(() => {
    setFarmProcessingSitesDraft(formatFarmProcessingSitesDraft(selectedFarm ?? undefined))
  }, [selectedFarm])

  useEffect(() => {
    setOpenUnitId(null)
    if (!selectedFarm) {
      setFarmNameDraft('')
      setFacilityTypeDraft('farm')
      setAltitudeDraft('0')
      return
    }

    setFarmNameDraft(selectedFarm.name)
    setFacilityTypeDraft(resolveFacilityType(selectedFarm))
    setAltitudeDraft(String(selectedFarm.altitude ?? 0))
  }, [selectedFarm?.id])

  const parsedFarmProcessingSitesDraft = React.useMemo(
    () => parseFarmProcessingSitesDraft(farmProcessingSitesDraft),
    [farmProcessingSitesDraft]
  )

  const getMutationStatusMessage = (
    kind: 'change' | 'delete',
    syncState: EntityMutationSyncState
  ): string => {
    if (kind === 'delete') {
      if (syncState === 'synced') return t('editFarm.status.deleteSynced')
      if (syncState === 'offline') return t('editFarm.status.deleteOffline')
      return t('editFarm.status.deletePending')
    }

    if (syncState === 'synced') return t('editFarm.status.changeSynced')
    if (syncState === 'offline') return t('editFarm.status.changeOffline')
    return t('editFarm.status.changePending')
  }

  const showStatus = (tone: 'info' | 'success' | 'error', message: string) => {
    statusVersionRef.current += 1
    setStatusMessage({ tone, message })
  }

  // Office UX v0 (findings #94): the General tab is one form with one
  // Guardar. Same rules as the per-field saves it replaces: a sensitive
  // field (type, altitude, time zone, species preset) asks for confirmation
  // first, and the change goes through `persistFarmChange` (one save).
  const parsedAltitudeDraft = Number.parseFloat(altitudeDraft)
  const nextAltitudeDraft = Number.isFinite(parsedAltitudeDraft) ? parsedAltitudeDraft : 0
  const trimmedTimeZoneDraft = farmTimeZoneDraft.trim()
  const isTimeZoneDraftInvalid = trimmedTimeZoneDraft.length > 0 && !isValidTimeZone(trimmedTimeZoneDraft)
  const draftIsProcessingPlant = facilityTypeDraft === 'processing-plant'
  const changedSensitiveFields: SensitiveFieldKey[] = []
  if (selectedFarm) {
    if (facilityTypeDraft !== selectedFacilityType) changedSensitiveFields.push('facilityType')
    if (nextAltitudeDraft !== selectedFarmAltitude) changedSensitiveFields.push('altitude')
    if (trimmedTimeZoneDraft !== selectedFarmTimeZone.trim()) changedSensitiveFields.push('timeZone')
    if (!draftIsProcessingPlant && farmSpeciesKeyDraft !== selectedFarmSpeciesKey) changedSensitiveFields.push('speciesPreset')
  }
  const farmNameChanged = Boolean(selectedFarm) && farmNameDraft.trim() !== selectedFarm?.name
  const farmCurrencyChanged = Boolean(selectedFarm) && farmCurrencyDraft !== selectedFarmCurrency
  const isGeneralDirty = farmNameChanged || farmCurrencyChanged || changedSensitiveFields.length > 0

  const resetGeneralDrafts = () => {
    if (!selectedFarm) return
    setFarmNameDraft(selectedFarm.name)
    setFacilityTypeDraft(selectedFacilityType)
    setAltitudeDraft(String(selectedFarmAltitude))
    setFarmTimeZoneDraft(selectedFarmTimeZone)
    setFarmSpeciesKeyDraft(selectedFarmSpeciesKey)
    setFarmCurrencyDraft(selectedFarmCurrency)
  }

  const saveGeneralDraft = async (): Promise<void> => {
    if (!canManageFarms || !selectedFarm) return
    const nextName = farmNameDraft.trim()
    if (nextName.length === 0 || isTimeZoneDraftInvalid || !isGeneralDirty) return
    const fields = [...changedSensitiveFields]
    if (fields.length > 0 && !(await confirmModal.ask({
      message: sensitiveChangeMessage(t, fields),
      tone: 'danger',
      confirmLabel: t('editFarm.confirm.confirmCta'),
      cancelLabel: t('common.cancel')
    }))) return

    const previousFarm = selectedFarm
    const nextMetadata: Record<string, unknown> = selectedFarm.metadata ? { ...selectedFarm.metadata } : {}
    let updatedFarm: Farm = { ...selectedFarm, name: nextName }
    if (fields.includes('facilityType')) {
      nextMetadata.facilityType = facilityTypeDraft
      updatedFarm = { ...updatedFarm, facilityType: facilityTypeDraft }
    }
    if (fields.includes('altitude')) {
      updatedFarm = { ...updatedFarm, altitude: nextAltitudeDraft }
    }
    if (fields.includes('timeZone')) {
      delete (nextMetadata as any).timezone
      delete (nextMetadata as any).timeZone
      delete (nextMetadata as any).tz
      if (trimmedTimeZoneDraft) {
        ;(nextMetadata as any).timezone = trimmedTimeZoneDraft
      }
    }
    if (fields.includes('speciesPreset')) {
      ;(nextMetadata as any).productionSpeciesKey = farmSpeciesKeyDraft
    }
    // findings #99: facility default currency (not sensitive: saved records keep their own).
    if (farmCurrencyChanged) {
      if (farmCurrencyDraft) nextMetadata.currency = farmCurrencyDraft
      else delete nextMetadata.currency
    }
    updatedFarm = { ...updatedFarm, metadata: nextMetadata }

    const updatedFarms = farms.map((farm) => (farm.id === selectedFarm.id ? updatedFarm : farm))
    applyFarmState(updatedFarms, selectedFarm.id)

    setIsApplyingMutation(true)
    try {
      const saved = await persistFarmChange(updatedFarm, previousFarm)
      if (!saved) return
      setFarmNameDraft(nextName)
      setAltitudeDraft(String(nextAltitudeDraft))
    } finally {
      setIsApplyingMutation(false)
    }

    if (farmNameChanged) {
      logEvent('action', 'farm_name_edited', { farmId: selectedFarm.id, newName: nextName })
    }
  }

  const saveProcessingSitesDraft = () => {
    if (!canManageFarms) return
    if (!selectedFarm) return
    if (parsedFarmProcessingSitesDraft.invalidLines.length > 0) return

    const normalizedDraft = formatFarmProcessingSitesDraft({
      metadata: parsedFarmProcessingSitesDraft.metadataValue.length > 0
        ? { processingSites: parsedFarmProcessingSitesDraft.metadataValue }
        : {}
    })
    const currentDraft = formatFarmProcessingSitesDraft(selectedFarm)
    if (normalizedDraft === currentDraft) {
      return
    }

    const previousFarm = selectedFarm
    const nextMetadata: Record<string, unknown> = selectedFarm.metadata ? { ...selectedFarm.metadata } : {}
    if (parsedFarmProcessingSitesDraft.metadataValue.length > 0) {
      nextMetadata.processingSites = parsedFarmProcessingSitesDraft.metadataValue
    } else {
      delete nextMetadata.processingSites
    }

    const updatedFarm: Farm = { ...selectedFarm, metadata: nextMetadata }
    const updatedFarms = farms.map((farm) => (farm.id === selectedFarm.id ? updatedFarm : farm))
    applyFarmState(updatedFarms, selectedFarm.id)
    setFarmProcessingSitesDraft(normalizedDraft)

    setIsApplyingMutation(true)
    void (async () => {
      try {
        await persistFarmChange(updatedFarm, previousFarm)
      } finally {
        setIsApplyingMutation(false)
      }
    })()
  }

  const activeFacilityTab: FacilityTab = facilityTab === 'units' && selectedFacilityIsProcessingPlant ? 'general' : facilityTab
  const openUnit = openUnitId ? (selectedFarm?.productionUnits.find((pu) => pu.id === openUnitId) ?? null) : null
  const closeUnitPanel = () => {
    setOpenUnitId(null)
    setEditingPU('')
    setPuNameDraft('')
    setEditingPUCapacity('')
    setPuCapacityDraft('')
  }

  const applyMutationStatus = (
    kind: 'change' | 'delete',
    result: Pick<EditFarmMutationResult | DeleteFarmMutationResult, 'syncState' | 'syncPromise'>
  ) => {
    const nextVersion = statusVersionRef.current + 1
    statusVersionRef.current = nextVersion

    const initialState = reconcileSyncMutationState({
      mutationSyncState: result.syncState,
      outboxStatus: facilitySyncStatusRef.current
    })

    setStatusMessage({
      tone: initialState === 'synced' ? 'success' : 'info',
      message: getMutationStatusMessage(kind, initialState)
    })

    if (!result.syncPromise) return

    void result.syncPromise.then(async (nextState) => {
      if (statusVersionRef.current !== nextVersion) return

      // v2.1 finding #61 (WSL-09): reconcile the attention-banner outbox
      // snapshot so it flips from 'pending' to 'synced' immediately after
      // the drain. Placed BEFORE the `nextState === result.syncState`
      // early-return so the refresh always fires — mutation-state parity
      // is orthogonal to outbox-drain progress. Without this, finding
      // #58's `showActionMessage` rule keeps the stale attention banner
      // pinned while suppressing the post-save toast — operator-visible
      // regression is "save appears stuck".
      const refreshedStatus = await refreshFacilitySyncStatusFnRef.current()
      const reconciledState = reconcileSyncMutationState({
        mutationSyncState: nextState,
        outboxStatus: refreshedStatus ?? facilitySyncStatusRef.current
      })

      if (reconciledState === initialState) return

      setStatusMessage({
        tone: reconciledState === 'synced' ? 'success' : 'info',
        message: getMutationStatusMessage(kind, reconciledState)
      })
    })
  }

  const applyFarmState = (updatedFarms: Farm[], preferredFarmId: string | null = selectedFarm?.id ?? null) => {
    setFarms(updatedFarms)

    const nextSelected = preferredFarmId
      ? updatedFarms.find((farm) => farm.id === preferredFarmId) ?? updatedFarms[0] ?? null
      : updatedFarms[0] ?? null

    setSelectedFarm(nextSelected)
  }

  const persistFarmChange = async (nextFarm: Farm, previousFarm: Farm | null): Promise<boolean> => {
    try {
      const result = await saveEditedFarm(nextFarm, previousFarm)
      applyMutationStatus('change', result)
      return true
    } catch (error) {
      console.warn('Failed to persist farm update:', error)
      await loadFarms()
      showStatus('error', t('editFarm.status.saveFailed'))
      return false
    }
  }

  const persistFarmDeletion = async (farm: Farm): Promise<boolean> => {
    try {
      const result = await deleteEditedFarm(farm)
      applyMutationStatus('delete', result)
      return true
    } catch (error) {
      console.warn('Failed to delete farm:', error)
      await loadFarms()
      showStatus('error', t('editFarm.status.deleteFailed'))
      return false
    }
  }

  const runSyncNow = async (): Promise<void> => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      showStatus('info', t('editFarm.sync.retryOffline'))
      return
    }

    setIsSyncingNow(true)
    showStatus('info', t('editFarm.sync.refreshing'))
    try {
      const preferredFarmId = selectedFarm?.id ?? null
      const result = await syncEntityOutboxNow({ maxEntries: 200 })
      await loadFarms({ forceRefresh: true, preferredFarmId })
      const status = await refreshFacilitySyncStatus(selectedFacilitySyncTarget)
      applyFacilitySyncOutcome(status, result.skippedNotFound)

      if (shouldPublishRefreshComplete(status.syncState)) {
        showStatus('success', t('editFarm.sync.refreshComplete'))
      }
    } catch {
      showStatus('error', t('editFarm.sync.retryFailed'))
    } finally {
      setIsSyncingNow(false)
    }
  }

  // Export data function
  const exportData = async () => {
    try {
      const tenantKey = getActiveTenantKey()

      await ensureOfflineReady()
      // On-demand mirror for export portability: include lots mirror keys in the exported payload.
      await syncLotsFromDbIntoLocalStorage()
      const [farmsForExport, tasksFromDb] = await Promise.all([
        dexieFarmRepo.list(),
        dexieTaskRepo.list()
      ])

      const allData = await buildSundayAppExportPayload({
        tenantKey,
        farms: farmsForExport,
        tasksFromDb
      })
      
      const dataStr = JSON.stringify(allData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = `sunday-app-data-${farmToday()}.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      alert(t('editFarm.alert.exportSuccess'))
    } catch (error) {
      console.error('Export failed:', error)
      alert(t('editFarm.alert.exportFailed'))
    }
  }

  // Import data function
  const importData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      void (async () => {
        try {
          const data = JSON.parse(e.target?.result as string)

          const importedProductionRecordEntries: Array<{ entityType: 'productionRecord'; op: 'upsert'; entityId: string; payload: unknown }> = []

          const toOptionalFiniteNumberString = (value: unknown): string | undefined => {
            if (value === null || value === undefined) return undefined
            if (typeof value === 'number') {
              return Number.isFinite(value) ? String(value) : undefined
            }
            if (typeof value === 'string') {
              const trimmed = value.trim()
              if (!trimmed) return undefined
              const parsed = Number(trimmed)
              return Number.isFinite(parsed) ? trimmed : undefined
            }
            return undefined
          }

          const toOptionalTrimmedString = (value: unknown): string | undefined => {
            if (typeof value !== 'string') return undefined
            const trimmed = value.trim()
            return trimmed ? trimmed : undefined
          }

          const isValidIsoDate = (value: string): boolean => {
            const trimmed = String(value ?? '').trim()
            if (!trimmed) return false
            const parsed = new Date(trimmed)
            return !Number.isNaN(parsed.getTime())
          }

          const isSyncableImportedProductionRow = (row: any): boolean => {
            const id = String(row?.id ?? '').trim()
            if (!id) return false
            const lot = String(row?.lot ?? '').trim()
            if (!lot) return false
            const stage = String(row?.stage ?? '').trim()
            if (stage !== 'Active' && stage !== 'Inactive') return false
            const date = String(row?.date ?? '').trim()
            if (date && !isValidIsoDate(date)) return false
            return true
          }

          const toSyncableImportedWaterRows = (value: unknown): any[] => {
            if (!Array.isArray(value) || value.length === 0) return []
            return value
              .filter((row) => row && typeof row === 'object')
              .filter((row) => {
                const id = String((row as any).id ?? '').trim()
                if (!id) return false
                const date = String((row as any).date ?? '').trim()
                if (!date) return false
                return isValidIsoDate(date)
              })
          }

          const tenantKey = getActiveTenantKey()
          const importTenantKey = typeof data?.tenantKey === 'string' ? data.tenantKey : null
          if (importTenantKey && importTenantKey !== tenantKey) {
            const ok = await confirmModal.ask({
              message: t('editFarm.confirm.tenantMismatch', { importTenantKey, tenantKey }),
              tone: 'danger',
              confirmLabel: t('editFarm.confirm.confirmCta'),
              cancelLabel: t('common.cancel')
            })
            if (!ok) return
          }
        
          const hasFarmsPayload = data.farms && Array.isArray(data.farms)
          const hasTasksPayload = data.tasks && Array.isArray(data.tasks.items)

          if (hasFarmsPayload || hasTasksPayload) {
            const waterDataCount = data.waterData ? Object.keys(data.waterData).length : 0
            const otherDataCount = data.otherData ? Object.keys(data.otherData).length : 0
            const tasksCount = data.tasks?.items?.length ?? 0
            
            // Confirm before importing
            if (await confirmModal.ask({
              message: t('editFarm.confirm.importSummary', {
                facilities: (data.farms?.length ?? 0),
                tasks: tasksCount,
                water: waterDataCount,
                other: otherDataCount
              }),
              tone: 'default',
              confirmLabel: t('editFarm.confirm.importCta'),
              cancelLabel: t('common.cancel')
            })) {
              // Import farms
              if (hasFarmsPayload) {
                try {
                  await ensureOfflineReady()
                  await dexieFarmRepo.replaceAll(data.farms)
                } catch (error) {
                  console.error('Farm import failed:', error)
                }
              }

              if (hasTasksPayload) {
                try {
                  await ensureOfflineReady()
                  await dexieTaskRepo.replaceAll(data.tasks.items)
                } catch (error) {
                  console.error('Task import failed:', error)
                }
              }

              // Import production table rows into Dexie when a legacy blob is present.
              // This avoids the app falling back to any stale or mixed legacy tableData.
              if (hasFarmsPayload && data?.tableData && typeof data.tableData === 'object') {
                try {
                  await ensureOfflineReady()

                  const farmsFromImport = Array.isArray(data.farms) ? data.farms : []
                  const tableData: Record<string, any> = data.tableData

                  for (const farm of farmsFromImport) {
                    if (!farm?.id || !farm?.name) continue
                    const pus = Array.isArray(farm.productionUnits) ? farm.productionUnits : []

                    for (const pu of pus) {
                      if (!pu?.id || !pu?.name) continue
                      const context = {
                        farmId: String(farm.id),
                        productionUnitId: String(pu.id),
                        farmName: String(farm.name),
                        productionUnitName: String(pu.name)
                      }

                      const nameBucketKey = `${context.farmName}-${context.productionUnitName}`
                      // Historic exports used multiple bucket-key formats.
                      // v1/v2 commonly used farmId-productionUnitId.
                      const idBucketKeyDash = `${context.farmId}-${context.productionUnitId}`
                      const idBucketKeyColon = `${context.farmId}::${context.productionUnitId}`
                      const rawRows =
                        (Array.isArray(tableData[idBucketKeyDash]) && tableData[idBucketKeyDash]) ||
                        (Array.isArray(tableData[idBucketKeyColon]) && tableData[idBucketKeyColon]) ||
                        (Array.isArray(tableData[nameBucketKey]) && tableData[nameBucketKey]) ||
                        []

                      if (rawRows.length === 0) continue

                      // Manual lot mapping mode: do not auto-map lots on import.
                      // Persist the raw rows exactly as exported; the user can map lots explicitly in the UI.
                      await replaceProductionUnitTableRows(context as any, rawRows)

                      // Also queue these rows to sync into Prisma as ProductionRecord.
                      // `replaceProductionUnitTableRows` intentionally does not enqueue outbox entries.
                      for (const row of rawRows) {
                        if (!isSyncableImportedProductionRow(row)) continue
                        const rowId = String((row as any).id)
                        importedProductionRecordEntries.push({
                          entityType: 'productionRecord',
                          op: 'upsert',
                          entityId: rowId,
                          payload: toProductionRecordPayload(context as any, row as any)
                        })
                      }
                    }
                  }
                } catch (error) {
                  console.error('Production table import failed:', error)
                }
              }
            
            // Import table/localStorage data if available (with data validation)
            applyImportedStoragePayload({ tenantKey, data })

            // If lots were carried via localStorage payload, sync them into Dexie now.
            const importedLots = readLotsFromLocalStorage(tenantKey)
            if (importedLots.length > 0) {
              try {
                await syncLotsFromLocalStorageIntoDb()
              } catch (error) {
                console.error('Lots import sync failed:', error)
              }
            }

            // Some exports (or older backups) may not include farm.productionUnits.
            // Rebuild missing production units from lots so selectors remain usable offline.
            await repairFarmsMissingProductionUnitsFromLots()

            // Queue imported entities to sync into the API when online.
            // Preserve IDs from the import and use names from JSON when present.
            if (hasFarmsPayload || importedLots.length > 0) try {
              await ensureOfflineReady()
              const [farmsFromDb, lotsFromDb] = await Promise.all([
                dexieFarmRepo.list(),
                dexieLotRepo.list()
              ])

              const entries: Array<{ entityType: any; op: any; entityId: string; payload?: unknown }> = []

              const importedWaterData = (data?.waterData && typeof data.waterData === 'object')
                ? (data.waterData as Record<string, unknown>)
                : null

              const importedOtherData = (data?.otherData && typeof data.otherData === 'object')
                ? (data.otherData as Record<string, unknown>)
                : null

              const importedWaterRowsByBaseKey = new Map<string, any[]>()
              if (importedWaterData) {
                for (const [rawKey, rawValue] of Object.entries(importedWaterData)) {
                  if (!rawKey.startsWith('water-')) continue
                  const baseKey = rawKey.split('::')[0]
                  if (!baseKey) continue
                  // Migration flags are timestamps, not water row arrays.
                  if (baseKey.startsWith('water-migrated-')) continue
                  // Prefer canonical v2 keys for API sync.
                  if (!baseKey.startsWith('water-v2-')) continue

                  const rows = toSyncableImportedWaterRows(rawValue)
                  if (rows.length === 0) continue
                  importedWaterRowsByBaseKey.set(baseKey, rows)
                }
              }

              // Queue imported tasks to sync into Prisma (Tasks are API-backed but were previously best-effort).
              if (hasTasksPayload && Array.isArray(data?.tasks?.items)) {
                for (const task of data.tasks.items as any[]) {
                  const taskId = String(task?.id ?? '').trim()
                  const title = String(task?.title ?? '').trim()
                  if (!taskId || !title) continue
                  entries.push({
                    entityType: 'task',
                    op: 'upsert',
                    entityId: taskId,
                    payload: task
                  })
                }
              }

              // Queue imported cached SFR matrices (if present) so they persist to Prisma when online.
              const isValidMatrixPayload = (value: unknown): value is { temperatures: unknown; weights: unknown; values: unknown } => (
                Boolean(value)
                && typeof value === 'object'
                && !Array.isArray(value)
                && 'temperatures' in (value as any)
                && 'weights' in (value as any)
                && 'values' in (value as any)
              )

              if (importedOtherData) {
                for (const [key, value] of Object.entries(importedOtherData)) {
                  if (!key.startsWith('sfrMatrixData:')) continue
                  // Expect tenant-scoped keys: sfrMatrixData:global::equipoa
                  const baseKey = key.split('::')[0]
                  if (!baseKey) continue
                  const species = baseKey.slice('sfrMatrixData:'.length).trim()
                  if (!species) continue
                  if (!isValidMatrixPayload(value)) continue

                  entries.push({
                    entityType: 'sfrMatrix',
                    op: 'upsert',
                    entityId: species,
                    payload: {
                      species,
                      matrix: value
                    }
                  })
                }
              }

              for (const farm of Array.isArray(farmsFromDb) ? farmsFromDb : []) {
                if (!farm || typeof farm !== 'object') continue
                const farmId = String((farm as any).id ?? '').trim()
                const farmName = String((farm as any).name ?? '').trim()
                if (!farmId || !farmName) continue

                entries.push({
                  entityType: 'farm',
                  op: 'upsert',
                  entityId: farmId,
                  payload: {
                    id: farmId,
                    name: farmName,
                    altitude: (farm as any).altitude,
                    location: (farm as any).location,
                    metadata: (farm as any).metadata
                  }
                })

                const pus = Array.isArray((farm as any).productionUnits) ? (farm as any).productionUnits : []
                for (const pu of pus) {
                  const puId = String(pu?.id ?? '').trim()
                  const puName = String(pu?.name ?? '').trim()
                  if (!puId || !puName) continue
                  const puFarmId = String(pu?.farmId ?? farmId).trim()
                  if (!puFarmId) continue

                  entries.push({
                    entityType: 'productionUnit',
                    op: 'upsert',
                    entityId: puId,
                    payload: {
                      id: puId,
                      farmId: puFarmId,
                      name: puName,
                      altitude: pu?.altitude,
                      metadata: pu?.metadata
                    }
                  })

                  // If waterData was included in the import, sync canonical water rows to Prisma as WaterLog.
                  const waterBaseKey = `water-v2-${farmId}-${puId}`
                  const waterRows = importedWaterRowsByBaseKey.get(waterBaseKey)
                  if (waterRows && waterRows.length > 0) {
                    for (const row of waterRows) {
                      const rowId = String((row as any).id ?? '').trim()
                      if (!rowId) continue
                      const recordedAt = String((row as any).date ?? '').trim()
                      if (!recordedAt) continue

                      entries.push({
                        entityType: 'waterLog',
                        op: 'upsert',
                        entityId: rowId,
                        payload: {
                          id: rowId,
                          farmId,
                          productionUnitId: puId,
                          lot: toOptionalTrimmedString((row as any).lot),
                          recordedAt,
                          temperatureC: toOptionalFiniteNumberString((row as any).temperature),
                          ph: toOptionalFiniteNumberString((row as any).ph),
                          dissolvedOxygenMgL: toOptionalFiniteNumberString((row as any).dissolvedOxygen),
                          dissolvedOxygenSaturationPct: toOptionalFiniteNumberString((row as any).dissolvedOxygenSaturation),
                          manualDissolvedOxygenMgL: toOptionalFiniteNumberString((row as any).manualDissolvedOxygen),
                          manualDissolvedOxygenSaturationPct: toOptionalFiniteNumberString((row as any).manualDissolvedOxygenSaturation),
                          salinityPpt: toOptionalFiniteNumberString((row as any).salinity),
                          notes: toOptionalTrimmedString((row as any).notes)
                        }
                      })
                    }
                  }
                }
              }

              for (const lot of Array.isArray(lotsFromDb) ? lotsFromDb : []) {
                const lotId = String((lot as any)?.id ?? '').trim()
                const farmId = String((lot as any)?.farmId ?? '').trim()
                const productionUnitId = String((lot as any)?.productionUnitId ?? '').trim()
                const lotKey = String((lot as any)?.lotKey ?? '').trim()
                const stage = String((lot as any)?.stage ?? '').trim()

                if (!lotId || !farmId || !productionUnitId || !lotKey || !stage) continue

                entries.push({
                  entityType: 'lot',
                  op: 'upsert',
                  entityId: lotId,
                  payload: lot
                })
              }

              await enqueueEntityChanges([...entries, ...importedProductionRecordEntries] as any)

              // Kick off a few sync batches immediately when online.
              if (typeof navigator !== 'undefined' && navigator.onLine) {
                void (async () => {
                  try {
                    for (let i = 0; i < 5; i += 1) {
                      const { processed, remaining } = await syncEntityOutboxNow({ maxEntries: 200 })
                      if (remaining <= 0 || processed <= 0) break
                    }
                  } catch {
                    // Keep import UX stable even if sync fails to start.
                  }
                })()
              }
            } catch (error) {
              console.warn('[editFarm] failed to queue imported entities for sync', error)
            }
            
              // Reload data
              await loadFarms()
              alert(t('editFarm.alert.importSuccess'))
            }
          } else {
            alert(t('editFarm.alert.invalidDataFormat'))
          }
        } catch (error) {
          console.error('Import failed:', error)
          alert(t('editFarm.alert.importFailed'))
        }
      })()
    }
    reader.readAsText(file)
    
    // Clear the input so the same file can be selected again
    event.target.value = ''
  }

  const handleEditPUName = async (farmId: string, puId: string, newName: string) => {
    const previousFarm = farms.find((farm) => farm.id === farmId) ?? null
    if (!previousFarm) return
    // Findings #127: unit names are what the crew taps and what records show —
    // two units with the same name in one facility are blocked.
    const duplicate = previousFarm.productionUnits.some((pu) => pu.id !== puId && normalizeUnitName(pu.name) === normalizeUnitName(newName))
    if (duplicate) {
      setPuNameError(t('editFarm.productionUnits.duplicateName', { name: newName }))
      return
    }
    setPuNameError(null)

    const updated = {
      ...previousFarm,
      productionUnits: previousFarm.productionUnits.map((pu) =>
        pu.id === puId ? { ...pu, name: newName } : pu
      )
    }
    const updatedFarms = farms.map((farm) => (farm.id === farmId ? updated : farm))
    applyFarmState(updatedFarms, farmId)
    setEditingPU('')
    setPuNameDraft('')

    setIsApplyingMutation(true)
    try {
      await persistFarmChange(updated, previousFarm)
    } finally {
      setIsApplyingMutation(false)
    }

    logEvent('action', 'pu_name_edited', { farmId, puId, newName })
  }

  const handleEditPUCapacity = async (farmId: string, puId: string, newCapacity: string, newCapacityUnit: string) => {
    const capacity = newCapacity ? Number.parseFloat(newCapacity) : undefined
    const previousFarm = farms.find((farm) => farm.id === farmId) ?? null
    if (!previousFarm) return

    const updated = {
      ...previousFarm,
      productionUnits: previousFarm.productionUnits.map((pu) =>
        pu.id === puId ? { ...pu, capacity, capacityUnit: newCapacityUnit } : pu
      )
    }
    const updatedFarms = farms.map((farm) => (farm.id === farmId ? updated : farm))
    applyFarmState(updatedFarms, farmId)

    setIsApplyingMutation(true)
    try {
      await persistFarmChange(updated, previousFarm)
    } finally {
      setIsApplyingMutation(false)
    }

    logEvent('action', 'pu_capacity_edited', { farmId, puId, newCapacity, newCapacityUnit })
    setEditingPUCapacity('')
    setPuCapacityDraft('')
  }

  // findings #76 item 2 / Field Bundle v0 companion changes: unit and
  // facility settings live in `metadata` (no migration).
  const persistUnitsChange = async (farmId: string, mapUnits: (units: ProductionUnit[]) => ProductionUnit[]) => {
    const previousFarm = farms.find((farm) => farm.id === farmId) ?? null
    if (!previousFarm) return
    const updated = { ...previousFarm, productionUnits: mapUnits(previousFarm.productionUnits) }
    applyFarmState(farms.map((farm) => (farm.id === farmId ? updated : farm)), farmId)
    setIsApplyingMutation(true)
    try {
      await persistFarmChange(updated, previousFarm)
    } finally {
      setIsApplyingMutation(false)
    }
  }

  const handleSetUnitInactive = async (farmId: string, puId: string, inactive: boolean) => {
    await persistUnitsChange(farmId, (units) => units.map((pu) => (pu.id === puId ? withStatus(pu, inactive) : pu)))
    logEvent('action', 'pu_status_changed', { farmId, puId, inactive })
  }

  // findings #101: the other units holding one of this unit's active lots.
  const planCopyTargets = (puId: string) => {
    // Findings #160: "Sembrar en varias unidades" makes one lot per unit sharing a lineage root.
    const lotGroup = (lot: { id: string; lineageRootId?: string | null }) => lot.lineageRootId || lot.id
    const lotIds = new Set(activeLotsForUnit(puId).map(lotGroup))
    if (lotIds.size === 0) return []
    return (selectedFarm?.productionUnits ?? [])
      .filter((pu) => pu.id !== puId && !isInactive(pu))
      .map((pu) => ({ pu, shared: activeLotsForUnit(pu.id).filter((lot) => lotIds.has(lotGroup(lot))) }))
      .filter(({ shared }) => shared.length > 0)
      .map(({ pu, shared }) => ({ id: pu.id, name: pu.name, lotKeys: shared.map((lot) => lot.lotKey) }))
  }

  const handleCopyUnitPlans = async (farmId: string, sourceId: string, targetIds: string[], copyKgPerDay: boolean) => {
    const farm = farms.find((candidate) => candidate.id === farmId)
    const source = farm?.productionUnits.find((pu) => pu.id === sourceId)
    if (!source) return
    await persistUnitsChange(farmId, (units) => units.map((pu) => (
      targetIds.includes(pu.id)
        ? copyUnitPlans(source, pu, { copyKgPerDay, targetLotIds: activeLotsForUnit(pu.id).map((lot) => lot.id) })
        : pu
    )))
    logEvent('action', 'pu_plans_copied', { farmId, sourceId, targets: targetIds.length, copyKgPerDay })
  }

  // Findings #217: the stop rules, oxygen settings and measuring point to any unit of the facility.
  const handleCopyUnitRules = async (farmId: string, sourceId: string, targetIds: string[], copyPoint: boolean) => {
    const farm = farms.find((candidate) => candidate.id === farmId)
    const source = farm?.productionUnits.find((pu) => pu.id === sourceId)
    if (!source) return
    await persistUnitsChange(farmId, (units) => units.map((pu) => (
      targetIds.includes(pu.id) ? copyRulesAndMeasuringPoint(source, pu, { copyPoint }) : pu
    )))
    logEvent('action', 'pu_rules_copied', { farmId, sourceId, targets: targetIds.length, copyPoint })
  }

  const handleSaveUnitPlans = async (farmId: string, puId: string, plans: UnitCrewPlans) => {
    await persistUnitsChange(farmId, (units) => units.map((pu) => (pu.id === puId ? withMeasuringPoint(withUnitPlans(pu, plans), plans.measuringPointId) : pu)))
    logEvent('action', 'pu_plans_changed', {
      farmId,
      puId,
      feedPlan: Boolean(plans.feedPlan),
      samplingPlan: Boolean(plans.samplingPlan),
      waterParameters: plans.waterParameters?.length ?? 0
    })
  }

  const handleSetUnitHiddenFromCrew = async (farmId: string, puId: string, hidden: boolean) => {
    await persistUnitsChange(farmId, (units) => units.map((pu) => (pu.id === puId ? withCrewSettings(pu, { crewHidden: hidden }) : pu)))
  }

  // Moving a unit renumbers every unit's Crew Mode position (0..n-1).
  const handleMoveUnitInCrewOrder = async (farmId: string, puId: string, direction: -1 | 1) => {
    await persistUnitsChange(farmId, (units) => {
      const ordered = sortUnitsForCrew(units)
      const index = ordered.findIndex((pu) => pu.id === puId)
      const target = index + direction
      if (index < 0 || target < 0 || target >= ordered.length) return units
      const swapped = [...ordered]
      ;[swapped[index], swapped[target]] = [swapped[target], swapped[index]]
      const position = new Map(swapped.map((pu, i) => [pu.id, i]))
      return units.map((pu) => withCrewSettings(pu, { crewOrder: position.get(pu.id) ?? null }))
    })
  }

  const handleSetFarmInactive = async (farmId: string, inactive: boolean) => {
    const previousFarm = farms.find((farm) => farm.id === farmId) ?? null
    if (!previousFarm) return
    const updated = withStatus(previousFarm, inactive)
    applyFarmState(farms.map((farm) => (farm.id === farmId ? updated : farm)), farmId)
    setIsApplyingMutation(true)
    try {
      await persistFarmChange(updated, previousFarm)
    } finally {
      setIsApplyingMutation(false)
    }
    logEvent('action', 'farm_status_changed', { farmId, inactive })
  }

  // Lot projections (findings #82): facility defaults in `metadata`.
  const handleSaveFacilityProjection = async (
    farmId: string,
    settings: { harvestTargetWeightG: number | null; projectionTgc: number | null }
  ) => {
    const previousFarm = farms.find((farm) => farm.id === farmId) ?? null
    if (!previousFarm) return
    const updated = withProjectionTgc(withHarvestTargetWeightG(previousFarm, settings.harvestTargetWeightG), settings.projectionTgc)
    applyFarmState(farms.map((farm) => (farm.id === farmId ? updated : farm)), farmId)
    setIsApplyingMutation(true)
    try {
      await persistFarmChange(updated, previousFarm)
    } finally {
      setIsApplyingMutation(false)
    }
    logEvent('action', 'farm_projection_settings_changed', { farmId })
  }

  // Unit type as a fixed list (findings #96); a free-text type from before stays until changed.
  const handleSetUnitType = async (farmId: string, puId: string, type: string) => {
    await persistUnitsChange(farmId, (units) => units.map((pu) => (pu.id === puId ? { ...pu, type: type || undefined } : pu)))
    logEvent('action', 'pu_type_changed', { farmId, puId, type })
  }

  // Hatchery v0 (findings #84): egg-lot defaults in `metadata`.
  const handleSaveFacilityHatchery = async (farmId: string, defaults: HatcheryDefaults) => {
    const previousFarm = farms.find((farm) => farm.id === farmId) ?? null
    if (!previousFarm) return
    const updated = withHatcheryDefaults(previousFarm, defaults)
    applyFarmState(farms.map((farm) => (farm.id === farmId ? updated : farm)), farmId)
    setIsApplyingMutation(true)
    try {
      await persistFarmChange(updated, previousFarm)
    } finally {
      setIsApplyingMutation(false)
    }
    logEvent('action', 'farm_hatchery_settings_changed', { farmId })
  }

  // Findings #210: Protocolo de traslado in `metadata`.
  const handleSaveTransferProtocol = async (farmId: string, bands: TransferProtocolBand[]) => {
    const previousFarm = farms.find((farm) => farm.id === farmId) ?? null
    if (!previousFarm) return
    const updated = withTransferProtocol(previousFarm, bands)
    applyFarmState(farms.map((farm) => (farm.id === farmId ? updated : farm)), farmId)
    setIsApplyingMutation(true)
    try {
      await persistFarmChange(updated, previousFarm)
    } finally {
      setIsApplyingMutation(false)
    }
    logEvent('action', 'farm_transfer_protocol_changed', { farmId })
  }

  // Ask the server what the unit / facility holds before offering a delete.
  const requestDelete = async (type: 'farm' | 'pu', id: string) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      showStatus('error', t('editFarm.delete.needsConnection'))
      return
    }
    setCheckingDelete(true)
    try {
      const holdings = type === 'farm' ? await api.farms.holdings(id) : await api.productionUnits.holdings(id)
      if (holdings.total > 0) {
        setBlockedDelete({ type, id, holdings })
        return
      }
      setShowDeleteConfirm({ type, id })
    } catch (error) {
      // Not on the server yet (created offline, never synced): nothing to lose.
      if (error instanceof ApiError && error.status === 404) {
        setShowDeleteConfirm({ type, id })
        return
      }
      showStatus('error', t('editFarm.delete.checkFailed'))
    } finally {
      setCheckingDelete(false)
    }
  }

  const handleDeleteFarm = async (farmId: string) => {
    if (!canManageFarms) return
    if (isDeleting) return

    const farmToDelete = farms.find((farm) => farm.id === farmId)
    if (!farmToDelete) return

    setIsDeleting(true)
    try {
      // Optimistic UI update immediately.
      const updatedFarms = farms.filter((farm) => farm.id !== farmId)
      applyFarmState(updatedFarms, null)
      setShowDeleteConfirm(null)

      // persistFarmDeletion -> deleteEditedFarm -> purgeLocalDataForFarm
      // already cleans up every local table (production-table/water rows,
      // lots, and the six event-log repos) for this farm and its PUs —
      // no separate cleanup needed here.
      await persistFarmDeletion(farmToDelete)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeletePU = async (farmId: string, puId: string) => {
    const farm = farms.find((candidate) => candidate.id === farmId)
    if (!farm) return

    const puToDelete = farm.productionUnits.find((pu) => pu.id === puId)
    if (!puToDelete) return

    const updatedFarm = {
      ...farm,
      productionUnits: farm.productionUnits.filter((pu) => pu.id !== puId)
    }
    const updatedFarms = farms.map((candidate) =>
      candidate.id === farmId ? updatedFarm : candidate
    )
    applyFarmState(updatedFarms, farmId)

    setShowDeleteConfirm(null)

    setIsApplyingMutation(true)
    try {
      const saved = await persistFarmChange(updatedFarm, farm)
      if (!saved) return

      deleteProductionUnitTableDataset({
        farmId,
        productionUnitId: puId,
        farmName: farm.name,
        productionUnitName: puToDelete.name
      })

      deleteWaterTableRows({
        farmId,
        productionUnitId: puId,
        farmName: farm.name,
        productionUnitName: puToDelete.name
      })

      // The two calls above only ever cleaned production-table/water rows —
      // this PU's FeedLog/HarvestLog/LotCostEvent/MortalityLog/
      // WeightSampleLog/FishStockLog rows and its own lots leaked the same
      // way a farm delete's did (see entityCachePurge.ts).
      void purgeLocalDataForProductionUnit(farmId, puId)
    } finally {
      setIsApplyingMutation(false)
    }
  }

  const handleAddPU = async (farmId: string) => {
    const farm = farms.find((candidate) => candidate.id === farmId)
    if (!farm) return

    const takenNames = new Set(farm.productionUnits.map((pu) => normalizeUnitName(pu.name)))
    let suffix = farm.productionUnits.length + 1
    while (takenNames.has(normalizeUnitName(`PU${suffix}`))) suffix += 1
    const newPU: ProductionUnit = {
      id: generateUuid(),
      farmId,
      name: `PU${suffix}`,
      createdAt: new Date().toISOString()
    }

    const updatedFarm = {
      ...farm,
      productionUnits: [...farm.productionUnits, newPU]
    }
    const updatedFarms = farms.map((candidate) =>
      candidate.id === farmId ? updatedFarm : candidate
    )
    applyFarmState(updatedFarms, farmId)
    // Findings #126: open the new unit with its name ready to type, instead of
    // leaving "PU6" somewhere in a long list.
    setOpenUnitId(newPU.id)
    setEditingPU(newPU.id)
    setPuNameDraft(newPU.name)
    setPuNameError(null)

    setIsApplyingMutation(true)
    try {
      await persistFarmChange(updatedFarm, farm)
    } finally {
      setIsApplyingMutation(false)
    }
  }

  const statusStyle = (tone: 'info' | 'success' | 'error'): React.CSSProperties => {
    const paletteByTone = {
      info: {
        borderColor: '#bae6fd',
        backgroundColor: '#f0f9ff',
        textColor: '#075985'
      },
      success: {
        borderColor: '#86efac',
        backgroundColor: '#f0fdf4',
        textColor: '#166534'
      },
      error: {
        borderColor: '#fca5a5',
        backgroundColor: '#fef2f2',
        textColor: '#b91c1c'
      }
    } as const
    const palette = paletteByTone[tone]

    return {
      marginTop: 12,
      padding: '12px 14px',
      borderRadius: 10,
      border: `1px solid ${palette.borderColor}`,
      background: palette.backgroundColor,
      color: palette.textColor,
      fontSize: 14,
      lineHeight: 1.5,
      fontWeight: 500
    }
  }

  return (
    <div className="responsive-container">
      <div className="app-header" style={{ padding: 0 }}>
        <h1>🏗️ {t('editFarm.title')}</h1>
        <p>{t('editFarm.subtitle')}</p>
      </div>

      <div className="responsive-toolbar">
        <div className="responsive-actions">
          <div ref={exportButtonRef}>
            <GradientButton
              onClick={() => {
                if (!canManageFarms) {
                  alert(t('editFarm.alert.readOnlyExport'))
                  return
                }
                exportData()
              }}
              disabled={!canManageFarms}
              variant="primary"
              size="sm"
              style={{ boxShadow: highlightExport ? '0 10px 20px rgba(0, 0, 0, 0.18)' : undefined }}
            >
              {t('editFarm.action.export')}
            </GradientButton>
          </div>

          <GradientButton
            onClick={() => {
              if (!canManageFarms) {
                alert(t('editFarm.alert.readOnlyImport'))
                return
              }
              importInputRef.current?.click()
            }}
            disabled={!canManageFarms}
            variant="info"
            size="sm"
          >
            {t('editFarm.action.import')}
          </GradientButton>
          <input
            ref={importInputRef}
            type="file"
            accept=".json"
            onChange={(event) => {
              if (!canManageFarms) {
                alert(t('editFarm.alert.readOnlyImport'))
                if (event.target) {
                  ;(event.target as HTMLInputElement).value = ''
                }
                return
              }
              importData(event)
            }}
            disabled={!canManageFarms}
            style={{ display: 'none' }}
          />
        </div>

        {selectedFarm && (
          <div className={styles.mobileChip}>
            {selectedFacilityIsProcessingPlant
              ? t('editFarm.chip.processingPlant', { facility: selectedFarm.name })
              : t('editFarm.chip.facilitySpecies', { facility: selectedFarm.name, species: selectedSpeciesLabel ?? '' })}
          </div>
        )}
      </div>

      {(() => {
        const syncBannerVisibility = computeSyncBannerVisibility({
          hasActionMessage: Boolean(statusMessage),
          hasAttentionMessage: Boolean(facilitySyncAttentionMessage),
          hasLastResultSummary: Boolean(facilitySyncLastResultSummary)
        })

        const showSyncBanner = Boolean(
          facilitySyncAttentionMessage
          || facilitySyncLastResultSummary
          || (statusMessage && syncBannerVisibility.showActionMessage)
        )

        if (!showSyncBanner) return null

        const syncBannerTone: 'info' | 'success' | 'error' =
          facilitySyncAttentionMessage ? 'info' : (statusMessage?.tone ?? 'info')

        let syncBannerContent: React.ReactNode = null

        if (facilitySyncAttentionMessage) {
          syncBannerContent = (
            <>
              <div>{facilitySyncAttentionMessage}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <GradientButton
                  type="button"
                  size="sm"
                  variant="info"
                  onClick={() => void handleFacilitySyncRetry()}
                  loading={isRetryingFacilitySync}
                  disabled={isRetryingFacilitySync || isSyncingNow}
                >
                  {isRetryingFacilitySync ? t('editFarm.sync.retrying') : t('editFarm.sync.retryAction')}
                </GradientButton>
                <GradientButton
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleFacilitySyncRefresh()}
                  loading={isSyncingNow}
                  disabled={isSyncingNow || isRetryingFacilitySync}
                >
                  {isSyncingNow ? t('editFarm.sync.refreshing') : t('editFarm.sync.refreshAction')}
                </GradientButton>
              </div>
            </>
          )
        } else if (facilitySyncLastResultSummary) {
          syncBannerContent = (
            <>
              <div>{facilitySyncLastResultSummary.resultLabel}</div>
              <div>{facilitySyncLastResultSummary.attemptedAtLabel}</div>
            </>
          )
        } else {
          syncBannerContent = <div>{statusMessage?.message}</div>
        }

        return (
          <div aria-live="polite" role="status" style={statusStyle(syncBannerTone)}>
            {syncBannerContent}
          </div>
        )
      })()}

      <div className={styles.layout}>
        <div className="form-card">
          <div className={styles.panelTitle}>
            <h3 style={{ margin: 0 }}>{t('editFarm.panel.facilitiesCount', { count: filteredFarms.length })}</h3>
          </div>

          <div className={styles.searchRow}>
            <input
              className={styles.searchInput}
              type="text"
              value={farmQuery}
              onChange={(e) => setFarmQuery(e.target.value)}
              placeholder={t('editFarm.search.placeholder')}
            />
            {farmQuery.trim().length > 0 && (
              <GradientButton
                variant="secondary"
                size="xs"
                onClick={() => setFarmQuery('')}
              >
                {t('editFarm.search.clear')}
              </GradientButton>
            )}
          </div>

          <div className={styles.scrollPanel}>
            {filteredFarms.length === 0 ? (
              <div className={styles.itemSubtle} style={{ padding: 8 }}>
                {t('editFarm.empty.noFacilities')}
              </div>
            ) : (
              filteredFarms.map((farm) => {
                const selected = selectedFarm?.id === farm.id
                return (
                  <button
                    key={farm.id}
                    type="button"
                    onClick={() => selectFarm(farm)}
                    className={`${styles.itemButton} ${selected ? styles.itemButtonSelected : ''}`}
                  >
                    <div style={{ fontWeight: 700 }}>{farm.name}</div>
                    <div className={styles.itemMeta}>{t('editFarm.item.pusCount', { count: farm.productionUnits.length })}</div>
                    <div className={styles.itemSubtle}>
                      {t('editFarm.item.created', { date: farm.createdAt ? new Date(farm.createdAt).toLocaleDateString() : t('editFarm.common.na') })}
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </div>

        <div>
          {selectedFarm ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {fellBackToFirstFarm && (
                <output
                  data-testid="edit-farm-first-fallback-banner"
                  style={{
                    display: 'block',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #fde68a',
                    backgroundColor: '#fffbeb',
                    color: '#92400e',
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  {t('editFarm.fallback.firstFarmShown')}
                </output>
              )}
              <div className={styles.detailsHeader}>
                <h3 style={{ margin: 0 }}>{t('editFarm.details.title')}</h3>
              </div>

              {/* Office UX v0 (findings #94): four tabs instead of one long page. */}
              <PillTabs<FacilityTab>
                ariaLabel={t('editFarm.tabs.ariaLabel')}
                testId="edit-farm-tabs"
                active={activeFacilityTab}
                onChange={setFacilityTab}
                tabs={[
                  { key: 'general', label: t('editFarm.tab.general'), testId: 'edit-farm-tab-general' },
                  ...(selectedFacilityIsProcessingPlant
                    ? []
                    : [{ key: 'units' as const, label: t('editFarm.tab.units'), count: selectedFarm.productionUnits.length, testId: 'edit-farm-tab-units' }]),
                  { key: 'culture', label: t('editFarm.tab.culture'), testId: 'edit-farm-tab-culture' },
                  { key: 'advanced', label: t('editFarm.tab.advanced'), testId: 'edit-farm-tab-advanced' }
                ]}
              />

              {activeFacilityTab === 'general' ? (
                <form
                  className="form-card"
                  data-testid="edit-farm-general-form"
                  onSubmit={(event) => {
                    event.preventDefault()
                    void saveGeneralDraft()
                  }}
                  style={{ display: 'grid', gap: 14 }}
                >
                  <div className={styles.sectionHeader} data-testid="edit-farm-block-identity">
                    <h4 className={styles.sectionTitle}>{t('editFarm.block.identity')}</h4>
                  </div>
                  <label className={styles.formField}>
                    <strong>{t('editFarm.details.facilityName')}</strong>
                    <input
                      type="text"
                      data-testid="edit-farm-name"
                      value={farmNameDraft}
                      onChange={(e) => setFarmNameDraft(e.target.value)}
                      disabled={!canManageFarms || isApplyingMutation || isDeleting}
                      className={styles.formInput}
                    />
                  </label>
                  <label className={styles.formField}>
                    <strong>🏷️ {t('editFarm.details.facilityType.label')}</strong>
                    <select
                      data-testid="edit-farm-facility-type"
                      value={facilityTypeDraft}
                      onChange={(event) => {
                        const nextFacilityType: FacilityType = event.target.value === 'processing-plant' ? 'processing-plant' : 'farm'
                        setFacilityTypeDraft(nextFacilityType)
                      }}
                      disabled={!canManageFarms || isApplyingMutation || isDeleting}
                      className={styles.formInput}
                    >
                      <option value="farm">{t('editFarm.details.facilityType.farm')}</option>
                      <option value="processing-plant">{t('editFarm.details.facilityType.processingPlant')}</option>
                    </select>
                    <span className={styles.itemMeta}>{t('editFarm.details.facilityType.meta')}</span>
                    <span className={styles.smallHelp}>
                      {t(draftIsProcessingPlant
                        ? 'editFarm.details.facilityType.helpProcessingPlant'
                        : 'editFarm.details.facilityType.helpFarm')}
                    </span>
                  </label>

                  <div className={styles.sectionHeader} data-testid="edit-farm-block-sensitive-config">
                    <h4 className={styles.sectionTitle}>{t('editFarm.block.sensitiveConfig')}</h4>
                  </div>
                  <label className={styles.formField}>
                    <strong>🏔️ {t('editFarm.details.altitude.label')} ({t('editFarm.details.altitude.unit')})</strong>
                    <input
                      type="number"
                      data-testid="edit-farm-altitude"
                      value={altitudeDraft}
                      onChange={(e) => setAltitudeDraft(e.target.value)}
                      disabled={!canManageFarms || isApplyingMutation || isDeleting}
                      min="0"
                      max="5000"
                      className={styles.formInput}
                      style={{ maxWidth: 160 }}
                    />
                    <span className={styles.smallHelp}>{t('editFarm.details.altitude.help')}</span>
                  </label>
                  <label className={styles.formField}>
                    <strong>🕒 {t('editFarm.details.timeZone.label')}</strong>
                    <input
                      type="text"
                      data-testid="edit-farm-time-zone"
                      value={farmTimeZoneDraft}
                      onChange={(e) => setFarmTimeZoneDraft(e.target.value)}
                      disabled={!canManageFarms || isApplyingMutation || isDeleting}
                      placeholder={resolveTimeZone(getBrowserTimeZone())}
                      className={styles.formInput}
                    />
                    <span className={styles.itemMeta}>{t('editFarm.details.timeZone.meta')}</span>
                    {isTimeZoneDraftInvalid ? (
                      <span className={styles.smallHelp} style={{ color: '#b91c1c' }}>
                        ❌ {t('editFarm.details.timeZone.invalid')}
                      </span>
                    ) : (
                      <span className={styles.smallHelp}>{t('editFarm.details.timeZone.help')}</span>
                    )}
                  </label>
                  {!draftIsProcessingPlant && (
                    <label className={styles.formField}>
                      <strong>🧬 {t('editFarm.details.speciesPreset.label')}</strong>
                      <select
                        data-testid="edit-farm-species-preset"
                        value={farmSpeciesKeyDraft}
                        onChange={(event) => setFarmSpeciesKeyDraft(normalizeSpeciesKey(event.target.value))}
                        disabled={!canManageFarms || isApplyingMutation || isDeleting}
                        className={styles.formInput}
                      >
                        {PRODUCTION_SPECIES_OPTIONS.map((option) => (
                          <option key={option.key} value={option.key}>
                            {getProductionSpeciesLabel(t, option.key)}
                          </option>
                        ))}
                      </select>
                      <span className={styles.itemMeta}>{t('editFarm.details.speciesPreset.meta')}</span>
                      <span className={styles.smallHelp}>
                        {getProductionSpeciesDescription(t, farmSpeciesKeyDraft) ||
                          t('editFarm.details.speciesPreset.helpFallback')}
                      </span>
                    </label>
                  )}

                  {!draftIsProcessingPlant && (
                    <label className={styles.formField}>
                      <strong>💱 {t('editFarm.details.currency.label')}</strong>
                      <select
                        data-testid="edit-farm-currency"
                        value={farmCurrencyDraft}
                        onChange={(event) => setFarmCurrencyDraft(event.target.value)}
                        disabled={!canManageFarms || isApplyingMutation || isDeleting}
                        className={styles.formInput}
                        style={{ maxWidth: 220 }}
                      >
                        <option value="">{t('editFarm.details.currency.none')}</option>
                        {FACILITY_CURRENCIES.map((code) => (
                          <option key={code} value={code}>{t(code === 'PEN' ? 'editFarm.details.currency.PEN' : 'editFarm.details.currency.USD')}</option>
                        ))}
                        {selectedFarmCurrency && !(FACILITY_CURRENCIES as readonly string[]).includes(selectedFarmCurrency) ? (
                          <option value={selectedFarmCurrency}>{selectedFarmCurrency}</option>
                        ) : null}
                      </select>
                      <span className={styles.smallHelp}>{t('editFarm.details.currency.help')}</span>
                    </label>
                  )}

                  <div className={styles.inlineActions} style={{ alignItems: 'center' }}>
                    <GradientButton
                      type="submit"
                      data-testid="edit-farm-general-save"
                      disabled={!canManageFarms || !isGeneralDirty || isApplyingMutation || isDeleting || isTimeZoneDraftInvalid || farmNameDraft.trim().length === 0}
                      loading={isApplyingMutation}
                      variant="success"
                      size="sm"
                    >
                      {t('common.save')}
                    </GradientButton>
                    <GradientButton
                      type="button"
                      data-testid="edit-farm-general-discard"
                      onClick={resetGeneralDrafts}
                      disabled={!isGeneralDirty || isApplyingMutation}
                      variant="secondary"
                      size="sm"
                    >
                      {t('editFarm.general.discard')}
                    </GradientButton>
                    {isGeneralDirty ? (
                      <span className={styles.itemSubtle}>{t('editFarm.general.unsaved')}</span>
                    ) : null}
                  </div>
                </form>
              ) : null}

              {activeFacilityTab === 'units' && !selectedFacilityIsProcessingPlant ? (
                <div className="form-card" data-testid="edit-farm-block-production-units">
                  <div className={styles.inlineRow} style={{ justifyContent: 'space-between', width: '100%' }}>
                    <div>
                      <h4 style={{ margin: 0 }}>{t('editFarm.productionUnits.title', { count: selectedFarm.productionUnits.length })}</h4>
                      <span className={styles.itemSubtle}>{t('editFarm.units.openHint')}</span>
                    </div>
                    <GradientButton onClick={() => void handleAddPU(selectedFarm.id)} disabled={isApplyingMutation || isDeleting} variant="success" size="sm">
                      {t('editFarm.productionUnits.addPu')}
                    </GradientButton>
                  </div>

                  {selectedFarm.productionUnits.length === 0 ? (
                    <div className={styles.itemSubtle} style={{ padding: 8 }}>
                      {t('editFarm.productionUnits.empty')}
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto', marginTop: 12 }}>
                      <table className={styles.unitTable} data-testid="edit-farm-units-table">
                        <thead>
                          <tr>
                            <th>{t('editFarm.units.col.name')}</th>
                            <th>{t('editFarm.units.col.type')}</th>
                            <th>{t('editFarm.productionUnits.volume')}</th>
                            <th>{t('editFarm.units.col.status')}</th>
                            <th>{t('editFarm.units.col.crewOrder')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortUnitsForCrew(selectedFarm.productionUnits).map((pu) => {
                            const position = crewPositionById.get(pu.id) ?? 0
                            return (
                              <tr
                                key={pu.id}
                                data-testid={`edit-farm-unit-row-${pu.id}`}
                                className={openUnitId === pu.id ? styles.unitRowSelected : undefined}
                                onClick={() => setOpenUnitId(pu.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  <button
                                    type="button"
                                    className={styles.linkButton}
                                    data-testid={`edit-farm-unit-open-${pu.id}`}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      setOpenUnitId(pu.id)
                                    }}
                                  >
                                    {pu.name}
                                  </button>
                                </td>
                                <td>{unitTypeLabel(pu.type)}</td>
                                <td>{pu.capacity ? `${pu.capacity} ${pu.capacityUnit || 'm³'}` : t('editFarm.productionUnits.notSet')}</td>
                                <td>
                                  <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                                    <span className={isInactive(pu) ? styles.badgeMuted : styles.badgeOk}>
                                      {isInactive(pu) ? t('editFarm.status.inactiveUnit') : t('editFarm.units.status.active')}
                                    </span>
                                    {isHiddenFromCrew(pu) ? <span className={styles.badgeMuted}>{t('editFarm.crew.hidden')}</span> : null}
                                  </span>
                                </td>
                                <td onClick={(event) => event.stopPropagation()}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ minWidth: 18, textAlign: 'right' }}>{position + 1}</span>
                                    <GradientButton
                                      size="xs"
                                      variant="secondary"
                                      aria-label={t('editFarm.crew.moveUp')}
                                      data-testid={`edit-farm-pu-crew-up-${pu.id}`}
                                      disabled={isApplyingMutation || position === 0}
                                      onClick={() => void handleMoveUnitInCrewOrder(selectedFarm.id, pu.id, -1)}
                                    >
                                      ↑
                                    </GradientButton>
                                    <GradientButton
                                      size="xs"
                                      variant="secondary"
                                      aria-label={t('editFarm.crew.moveDown')}
                                      data-testid={`edit-farm-pu-crew-down-${pu.id}`}
                                      disabled={isApplyingMutation || position >= selectedFarm.productionUnits.length - 1}
                                      onClick={() => void handleMoveUnitInCrewOrder(selectedFarm.id, pu.id, 1)}
                                    >
                                      ↓
                                    </GradientButton>
                                  </span>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}

              {activeFacilityTab === 'culture' ? (
                <div style={{ display: 'grid', gap: 12 }} data-testid="edit-farm-block-culture">
                  <div className="form-card">
                    <div className={styles.sectionHeader}>
                      <h4 className={styles.sectionTitle}>{t('editFarm.culture.defaultsTitle')}</h4>
                    </div>
                    <div className={styles.smallHelp}>{t('editFarm.culture.defaultsHelp')}</div>
                    <FacilityProjectionSettings
                      farm={selectedFarm}
                      disabled={isApplyingMutation || !canManageFarms}
                      onSave={(settings) => handleSaveFacilityProjection(selectedFarm.id, settings)}
                    />
                    <FacilityHatcherySettings
                      farm={selectedFarm}
                      disabled={isApplyingMutation || !canManageFarms}
                      onSave={(defaults) => handleSaveFacilityHatchery(selectedFarm.id, defaults)}
                    />
                    <FacilityTransferProtocol
                      farm={selectedFarm}
                      speciesLabel={resolveFarmProductionSpeciesKey(selectedFarm) ? getProductionSpeciesLabel(t, selectedFarmSpeciesKey) : null}
                      disabled={isApplyingMutation || !canManageFarms}
                      onSave={(bands) => handleSaveTransferProtocol(selectedFarm.id, bands)}
                    />
                    {/* Findings #151: the density grid moved to Configuración → Capacidad de carga. */}
                    <div data-testid="edit-farm-density-limits" style={{ marginTop: 12, display: 'grid', gap: 4 }}>
                      <strong>{t('editFarm.density.title')}</strong>
                      <span style={{ fontSize: 13, color: '#475569' }}>
                        {t('editFarm.density.movedHint')}{' '}
                        <button
                          type="button"
                          onClick={() => navigate('/carrying-capacity')}
                          style={{ border: 'none', background: 'none', padding: 0, color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}
                          data-testid="edit-farm-open-capacity"
                        >
                          {t('editFarm.density.openCapacity')}
                        </button>
                      </span>
                    </div>
                  </div>
                  <CrewModeFarmFlagsPanel farmId={String(selectedFarm.id)} disabled={!canManageFarms} />
                </div>
              ) : null}

              {activeFacilityTab === 'advanced' ? (
                <div style={{ display: 'grid', gap: 12 }} data-testid="edit-farm-block-advanced">
                  <div className="form-card">
                    <div className={styles.inlineRow} style={{ alignItems: 'flex-start' }}>
                      <strong>🏭 {t('editFarm.details.processingSites.label')}</strong>
                      <div style={{ display: 'grid', gap: 8, flex: '1 1 320px' }}>
                        <textarea
                          value={farmProcessingSitesDraft}
                          onChange={(event) => setFarmProcessingSitesDraft(event.target.value)}
                          onBlur={saveProcessingSitesDraft}
                          disabled={!canManageFarms || isApplyingMutation || isDeleting}
                          placeholder={t('editFarm.details.processingSites.placeholder')}
                          rows={4}
                          style={{
                            padding: '8px 10px',
                            border: '1px solid #cbd5f5',
                            borderRadius: 10,
                            minWidth: 280,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
                          }}
                        />
                        <span className={styles.itemMeta}>{t('editFarm.details.processingSites.meta')}</span>
                      </div>
                    </div>
                    {parsedFarmProcessingSitesDraft.invalidLines.length > 0 ? (
                      <div className={styles.smallHelp} style={{ color: '#b91c1c' }}>
                        ❌ {t('editFarm.details.processingSites.invalid', { count: parsedFarmProcessingSitesDraft.invalidLines.length })}
                      </div>
                    ) : (
                      <div className={styles.smallHelp}>
                        {t('editFarm.details.processingSites.help')}
                      </div>
                    )}
                  </div>

                  <div className="form-card" data-testid="edit-farm-block-maintenance">
                    <div className={styles.sectionHeader}>
                      <h4 className={styles.sectionTitle}>{t('editFarm.advanced.syncTitle')}</h4>
                    </div>
                    <div className={styles.inlineActions}>
                      <GradientButton
                        onClick={() => void runSyncNow()}
                        disabled={isSyncingNow || isRetryingFacilitySync}
                        variant="secondary"
                        size="sm"
                      >
                        {t('datatable.action.syncNow')}
                      </GradientButton>
                      <GradientButton
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => void handleFacilitySyncRefresh()}
                        loading={isSyncingNow}
                        disabled={isSyncingNow || isRetryingFacilitySync}
                      >
                        {isSyncingNow ? t('editFarm.sync.refreshing') : t('editFarm.sync.refreshAction')}
                      </GradientButton>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 14 }}>
                      <input
                        type="checkbox"
                        data-testid="edit-farm-facility-inactive"
                        checked={isInactive(selectedFarm)}
                        disabled={isApplyingMutation || !canManageFarms}
                        onChange={(event) => void handleSetFarmInactive(selectedFarm.id, event.target.checked)}
                      />
                      <span>
                        <strong>{t('editFarm.status.inactiveFacility')}</strong>
                        <span className={styles.itemSubtle} style={{ display: 'block' }}>{t('editFarm.status.inactiveFacilityHelp')}</span>
                      </span>
                    </label>
                  </div>

                  <div className={`form-card ${styles.dangerZone}`} data-testid="edit-farm-danger-zone">
                    <h4 className={styles.sectionTitle} style={{ color: '#b91c1c' }}>{t('editFarm.advanced.dangerTitle')}</h4>
                    <div className={styles.smallHelp}>{t('editFarm.advanced.dangerHelp')}</div>
                    <div className={styles.inlineActions} style={{ marginTop: 10 }}>
                      <GradientButton
                        onClick={() => void requestDelete('farm', selectedFarm.id)}
                        disabled={isDeleting || isApplyingMutation || !canManageFarms || checkingDelete}
                        variant="danger"
                        size="sm"
                      >
                        {t('editFarm.details.deleteFacility')}
                      </GradientButton>
                    </div>
                  </div>
                </div>
              ) : null}

              <Drawer
                open={Boolean(openUnit)}
                onClose={closeUnitPanel}
                title={openUnit ? t('editFarm.units.panelTitle', { unit: openUnit.name }) : ''}
              >
                {openUnit ? (
                  <div style={{ display: 'grid', gap: 14 }} data-testid={`edit-farm-unit-panel-${openUnit.id}`}>
                    <div className={styles.inlineRow}>
                      <strong>{t('editFarm.productionUnits.puName')}</strong>
                      {editingPU === openUnit.id ? (
                        <>
                          <input
                            type="text"
                            value={puNameDraft}
                            onChange={(e) => {
                              setPuNameDraft(e.target.value)
                              setPuNameError(null)
                            }}
                            onFocus={(e) => e.currentTarget.select()}
                            data-testid={`edit-farm-pu-name-input-${openUnit.id}`}
                            autoFocus
                            className={styles.formInput}
                            style={{ minWidth: 200 }}
                          />
                          <div className={styles.inlineActions}>
                            <GradientButton
                              onClick={() => {
                                const trimmed = puNameDraft.trim()
                                if (trimmed.length === 0) return
                                void handleEditPUName(selectedFarm.id, openUnit.id, trimmed)
                              }}
                              disabled={isApplyingMutation || isDeleting}
                              variant="success"
                              size="xs"
                            >
                              {t('common.save')}
                            </GradientButton>
                            <GradientButton
                              onClick={() => {
                                setEditingPU('')
                                setPuNameDraft('')
                                setPuNameError(null)
                              }}
                              disabled={isApplyingMutation || isDeleting}
                              variant="secondary"
                              size="xs"
                            >
                              {t('common.cancel')}
                            </GradientButton>
                          </div>
                        </>
                      ) : (
                        <>
                          <span>{openUnit.name}</span>
                          <GradientButton
                            onClick={() => {
                              setEditingPU(openUnit.id)
                              setPuNameDraft(openUnit.name)
                            }}
                            disabled={isApplyingMutation || isDeleting}
                            variant="info"
                            size="xs"
                          >
                            {t('common.edit')}
                          </GradientButton>
                        </>
                      )}
                    </div>
                    {puNameError && editingPU === openUnit.id ? (
                      <div role="alert" data-testid="edit-farm-pu-name-error" style={{ color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{puNameError}</div>
                    ) : null}
                    <div className={styles.itemSubtle}>
                      {t('editFarm.item.created', { date: openUnit.createdAt ? new Date(openUnit.createdAt).toLocaleDateString() : t('editFarm.common.na') })}
                    </div>

                    <label className={styles.inlineRow}>
                      <strong style={{ fontSize: 12 }}>{t('editFarm.productionUnits.type')}</strong>
                      <select
                        data-testid={`edit-farm-pu-type-${openUnit.id}`}
                        value={openUnit.type ?? ''}
                        onChange={(event) => void handleSetUnitType(selectedFarm.id, openUnit.id, event.target.value)}
                        disabled={!canManageFarms || isApplyingMutation || isDeleting}
                        className={styles.formInput}
                      >
                        <option value="">{t('editFarm.productionUnits.typeNone')}</option>
                        {UNIT_TYPES.map((type) => (
                          <option key={type} value={type}>{t(UNIT_TYPE_LABEL_KEY[type])}</option>
                        ))}
                        {openUnit.type && !isUnitType(openUnit.type) ? (
                          <option value={openUnit.type}>{t('editFarm.productionUnits.typeOther', { type: openUnit.type })}</option>
                        ) : null}
                      </select>
                    </label>

                    <div className={styles.inlineRow}>
                      <strong style={{ fontSize: 12 }}>{t('editFarm.productionUnits.volume')}</strong>
                      {editingPUCapacity === openUnit.id ? (
                        <>
                          <input
                            type="number"
                            value={puCapacityDraft}
                            onChange={(e) => setPuCapacityDraft(e.target.value)}
                            placeholder={t('editFarm.productionUnits.capacityPlaceholder')}
                            className={styles.formInput}
                            style={{ width: 120 }}
                          />
                          <select
                            value={puCapacityUnitDraft}
                            onChange={(e) => setPuCapacityUnitDraft(e.target.value)}
                            className={styles.formInput}
                          >
                            <option value="m3">m³</option>
                            <option value="L">L</option>
                            <option value="ha">ha</option>
                            <option value="units">units</option>
                          </select>
                          <div className={styles.inlineActions}>
                            <GradientButton
                              onClick={() =>
                                void handleEditPUCapacity(
                                  selectedFarm.id,
                                  openUnit.id,
                                  puCapacityDraft,
                                  puCapacityUnitDraft
                                )
                              }
                              disabled={isApplyingMutation || isDeleting}
                              variant="success"
                              size="xs"
                            >
                              {t('common.save')}
                            </GradientButton>
                            <GradientButton
                              onClick={() => {
                                setEditingPUCapacity('')
                                setPuCapacityDraft('')
                              }}
                              disabled={isApplyingMutation || isDeleting}
                              variant="secondary"
                              size="xs"
                            >
                              {t('common.cancel')}
                            </GradientButton>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className={styles.itemMeta}>
                            {openUnit.capacity ? `${openUnit.capacity} ${openUnit.capacityUnit || 'm³'}` : t('editFarm.productionUnits.notSet')}
                          </span>
                          <GradientButton
                            onClick={() => {
                              setEditingPUCapacity(openUnit.id)
                              setPuCapacityDraft(openUnit.capacity?.toString() || '')
                              setPuCapacityUnitDraft(openUnit.capacityUnit || 'm3')
                            }}
                            disabled={isApplyingMutation || isDeleting}
                            variant="secondary"
                            size="xs"
                          >
                            {t('editFarm.productionUnits.editVolume')}
                          </GradientButton>
                        </>
                      )}
                    </div>

                    <div style={{ display: 'grid', gap: 8 }} data-testid={`edit-farm-pu-settings-${openUnit.id}`}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          data-testid={`edit-farm-pu-inactive-${openUnit.id}`}
                          checked={isInactive(openUnit)}
                          disabled={isApplyingMutation || (!isInactive(openUnit) && activeLotKeysByUnit.has(openUnit.id))}
                          title={!isInactive(openUnit) && activeLotKeysByUnit.has(openUnit.id)
                            ? t('editFarm.status.unitHasActiveLot', { lots: activeLotKeysByUnit.get(openUnit.id)?.join(', ') ?? '' })
                            : t('editFarm.status.inactiveUnitHelp')}
                          onChange={(event) => void handleSetUnitInactive(selectedFarm.id, openUnit.id, event.target.checked)}
                        />
                        {t('editFarm.status.inactiveUnit')}
                      </label>
                      {!isInactive(openUnit) && activeLotKeysByUnit.has(openUnit.id) ? (
                        <span className={styles.itemSubtle}>{t('editFarm.status.unitHasActiveLot', { lots: activeLotKeysByUnit.get(openUnit.id)?.join(', ') ?? '' })}</span>
                      ) : null}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          data-testid={`edit-farm-pu-crew-hidden-${openUnit.id}`}
                          checked={isHiddenFromCrew(openUnit)}
                          disabled={isApplyingMutation}
                          onChange={(event) => void handleSetUnitHiddenFromCrew(selectedFarm.id, openUnit.id, event.target.checked)}
                        />
                        {t('editFarm.crew.hidden')}
                      </label>
                      <span className={styles.itemSubtle}>
                        {t('editFarm.crew.order', { position: (crewPositionById.get(openUnit.id) ?? 0) + 1 })}
                      </span>
                    </div>

                    <UnitCrewPlansEditor
                      unit={openUnit}
                      feedItems={planFeedItems}
                      activeLots={activeLotsForUnit(openUnit.id)}
                      disabled={isApplyingMutation || !canManageFarms}
                      onSave={(plans) => handleSaveUnitPlans(selectedFarm.id, openUnit.id, plans)}
                      copyTargets={planCopyTargets(openUnit.id)}
                      onCopy={(targetIds, copyKgPerDay) => handleCopyUnitPlans(selectedFarm.id, openUnit.id, targetIds, copyKgPerDay)}
                      facilityUnits={(selectedFarm.productionUnits ?? [])
                        .filter((pu) => pu.id !== openUnit.id && !isInactive(pu))
                        .map((pu) => ({ id: pu.id, name: pu.name }))
                        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))}
                      onCopyRules={(targetIds, copyPoint) => handleCopyUnitRules(selectedFarm.id, openUnit.id, targetIds, copyPoint)}
                    />

                    <div className={styles.dangerZone} style={{ padding: 12, borderRadius: 10 }}>
                      <GradientButton
                        onClick={() => void requestDelete('pu', openUnit.id)}
                        disabled={isApplyingMutation || isDeleting || checkingDelete}
                        variant="danger"
                        size="sm"
                      >
                        {t('editFarm.units.delete')}
                      </GradientButton>
                    </div>
                  </div>
                ) : null}
              </Drawer>
            </div>
          ) : (
            <div className="form-card">
              <h3 style={{ marginTop: 0 }}>{t('editFarm.empty.selectFacilityTitle')}</h3>
              <div className={styles.itemMeta}>{t('editFarm.empty.selectFacilityBody')}</div>
            </div>
          )}
        </div>
      </div>

      {blockedDelete && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard} data-testid="edit-farm-delete-blocked">
            <h3 style={{ marginTop: 0 }}>{t('editFarm.delete.blockedTitle')}</h3>
            <p>
              {t(blockedDelete.type === 'farm' ? 'editFarm.delete.blockedFarmBody' : 'editFarm.delete.blockedUnitBody', {
                count: blockedDelete.holdings.total
              })}
            </p>
            <p className={styles.itemSubtle}>{t('editFarm.delete.blockedWhy')}</p>
            <div className={styles.inlineActions} style={{ justifyContent: 'flex-end' }}>
              <GradientButton onClick={() => setBlockedDelete(null)} variant="secondary" size="sm">
                {t('common.cancel')}
              </GradientButton>
              <GradientButton
                data-testid="edit-farm-delete-blocked-inactivate"
                onClick={() => {
                  const target = blockedDelete
                  setBlockedDelete(null)
                  if (target.type === 'farm') void handleSetFarmInactive(target.id, true)
                  else if (selectedFarm) void handleSetUnitInactive(selectedFarm.id, target.id, true)
                }}
                disabled={isApplyingMutation}
                variant="primary"
                size="sm"
              >
                {t('editFarm.delete.markInactive')}
              </GradientButton>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <h3 style={{ marginTop: 0 }}>⚠️ {t('editFarm.modal.confirmDeletionTitle')}</h3>
            <p>
              {showDeleteConfirm.type === 'farm'
                ? t('editFarm.modal.confirmDeleteFarmBody')
                : t('editFarm.modal.confirmDeletePuBody')}
            </p>
            <p style={{ fontWeight: 700 }}>{t('editFarm.modal.cannotBeUndone')}</p>
            <div className={styles.inlineActions} style={{ justifyContent: 'flex-end' }}>
              <GradientButton onClick={() => setShowDeleteConfirm(null)} variant="secondary" size="sm">
                {t('common.cancel')}
              </GradientButton>
              <GradientButton
                onClick={() => {
                  if (showDeleteConfirm.type === 'farm') {
                    void handleDeleteFarm(showDeleteConfirm.id)
                  } else {
                    void handleDeletePU(selectedFarm!.id, showDeleteConfirm.id)
                  }
                }}
                disabled={isDeleting || isApplyingMutation}
                variant="danger"
                size="sm"
              >
                {t('common.delete')}
              </GradientButton>
            </div>
          </div>
        </div>
      )}

      {confirmModal.Confirm()}
    </div>
  )
}
